import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, CheckCircle, XCircle, Loader2, Clock, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { getAuditVerificationInfo, verifyAuditVote } from "../../lib/api";
import { LegalFooter } from "../../components/LegalFooter";

export default function AuditVerifyPage() {
  const { t, i18n } = useTranslation();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [idPublica, setIdPublica] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    const loadInfo = async () => {
      try {
        const res = await getAuditVerificationInfo(token);
        setInfo(res.data);
        setRemaining(res.data.remaining_attempts);
        if (res.data.language && res.data.language !== i18n.language) {
          i18n.changeLanguage(res.data.language);
        }
      } catch (err) {
        const detail = err.response?.data?.detail || t('errors.loadVerification');
        setError(detail);
      } finally {
        setLoading(false);
      }
    };
    loadInfo();
  }, [token]);

  const toggleOption = (optionTitle) => {
    setSelectedOptions(prev =>
      prev.includes(optionTitle) ? prev.filter(o => o !== optionTitle) : [...prev, optionTitle]
    );
  };

  const handleVerify = async () => {
    if (!idPublica.trim()) return;
    if (selectedOptions.length === 0) return;

    setVerifying(true);
    setResult(null);
    try {
      const res = await verifyAuditVote(token, {
        id_publica: idPublica.trim(),
        selected_options: selectedOptions
      });
      setResult(res.data);
      setRemaining(res.data.remaining_attempts);
    } catch (err) {
      const detail = err.response?.data?.detail || t('errors.verificationError');
      if (err.response?.status === 429) {
        setError(detail);
        setRemaining(0);
      } else if (err.response?.status === 410) {
        setError(detail);
      } else setResult({ verified: false, message: detail });
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="bg-white border border-red-200 rounded-lg p-8 max-w-md w-full text-center shadow-sm">
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-900 mb-2">{t('auditVerify.verifyError')}</h2>
          <p className="text-slate-600">{error}</p>
        </div>
        <div className="w-full max-w-md mt-4">
          <LegalFooter />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-lg w-full shadow-sm">
        <div className="text-center mb-6">
          <Shield className="h-10 w-10 text-blue-600 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900">{t('auditVerify.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('common.consulta')}: <strong>{info?.consulta_title}</strong></p>
        </div>

        {remaining !== null && remaining <= 0 ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <XCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
            <p className="text-red-700 font-medium">{t('auditVerify.noAttemptsLeft')}</p>
          </div>
        ) : (
          <>
            {result?.verified ? (
              <div className="space-y-4" data-testid="verification-success">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
                  <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
                  <p className="text-emerald-800 font-semibold text-lg mb-2">{t('auditVerify.voteVerified')}</p>
                  <p className="text-emerald-700 text-sm">{result.message}</p>
                  {result.voted_date && (
                    <div className="mt-4 bg-white/60 rounded-lg p-3 inline-block">
                      <div className="flex items-center gap-2 text-emerald-700">
                        <Clock className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          {t('auditVerify.votedOn', { date: result.voted_date, time: result.voted_time })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {result && !result.verified && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="verification-failed">
                    <div className="flex items-start gap-2">
                      <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-700 font-medium text-sm">{result.message}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('auditVerify.publicId')}</label>
                  <input
                    type="text"
                    value={idPublica}
                    onChange={(e) => setIdPublica(e.target.value)}
                    placeholder={t('auditVerify.enterPublicId')}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="audit-verify-id-publica"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('auditVerify.optionsVoted')}</label>
                  <div className="space-y-2">
                    {(info?.options || []).map((opt, idx) => {
                      const title = opt.title || opt;
                      const isSelected = selectedOptions.includes(title);
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => toggleOption(title)}
                          className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors ${
                            isSelected
                              ? 'bg-blue-50 border-blue-400 text-blue-800 font-medium'
                              : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                          }`}
                          data-testid={`audit-verify-option-${idx}`}
                        >
                          {title}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-slate-500">
                    {t('auditVerify.remainingAttempts')}: <strong>{remaining}</strong>
                  </p>
                  <Button
                    onClick={handleVerify}
                    disabled={verifying || !idPublica.trim() || selectedOptions.length === 0}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="audit-verify-submit"
                  >
                    {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
                    {t('auditVerify.verify')}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <div className="w-full max-w-lg mt-4">
        <LegalFooter />
      </div>
    </div>
  );
}
